import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { stripeService } from '../../services/stripeService';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';

import SubscriptionStatus from './components/SubscriptionStatus';
import PlanComparison from './components/PlanComparison';
import PaymentMethods from './components/PaymentMethods';
import InvoiceHistory from './components/InvoiceHistory';

const BillingDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/agent-dashboard' },
    { label: 'Billing', path: '/billing-dashboard' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'Plans', icon: 'Package' },
    { id: 'payment', label: 'Payment Methods', icon: 'CreditCard' },
    { id: 'invoices', label: 'Invoices', icon: 'FileText' }
  ];

  // Authentication guard - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('❌ User not authenticated, redirecting to login...');
      navigate('/user-login', { 
        state: { 
          from: '/billing-dashboard',
          message: 'Please log in to access billing settings' 
        } 
      });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Check for checkout success/cancel in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams?.get('success') === 'true') {
      setSuccessMessage('Subscription activated successfully! Welcome to Pro.');
      // Clean up URL
      window.history?.replaceState({}, '', '/billing-dashboard');
    } else if (urlParams?.get('canceled') === 'true') {
      setError('Checkout canceled. You can try again anytime.');
      window.history?.replaceState({}, '', '/billing-dashboard');
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadBillingData();
    }
  }, [user]);

  const loadBillingData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [plansResult, paymentMethodsResult, invoicesResult] = await Promise.all([
        stripeService?.getSubscriptionPlans(),
        stripeService?.getPaymentMethods(user?.id),
        stripeService?.getInvoices(user?.id)
      ]);

      if (plansResult?.error) throw new Error(plansResult.error);
      if (paymentMethodsResult?.error) throw new Error(paymentMethodsResult.error);
      if (invoicesResult?.error) throw new Error(invoicesResult.error);

      setPlans(plansResult?.data || []);
      setPaymentMethods(paymentMethodsResult?.data || []);
      setInvoices(invoicesResult?.data || []);
    } catch (err) {
      console.error('Load billing data error:', err);
      setError(err?.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (planId) => {
    const { error } = await stripeService?.changeSubscription(user?.id, planId);
    if (!error) {
      await loadBillingData();
    }
  };

  const handlePaymentMethodUpdate = async () => {
    await loadBillingData();
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Billing Dashboard - OfferSense</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Don't render anything if user is not authenticated (will redirect)
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Billing Dashboard - OfferSense</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Billing Dashboard - OfferSense</title>
        <meta name="description" content="Manage your subscription, payment methods, and billing history" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-6 md:py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Billing & Subscription
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Manage your subscription plan, payment methods, and billing history
          </p>
        </div>

        {successMessage && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-success font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-destructive font-medium">Error loading billing data</p>
              <p className="text-destructive/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              <SubscriptionStatus
                userProfile={userProfile}
                plans={plans}
                onUpgrade={() => setActiveTab('plans')}
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PaymentMethods
                  paymentMethods={paymentMethods}
                  userId={user?.id}
                  onUpdate={handlePaymentMethodUpdate}
                  compact
                />
                <InvoiceHistory
                  invoices={invoices?.slice(0, 5)}
                  compact
                  onViewAll={() => setActiveTab('invoices')}
                />
              </div>
            </>
          )}

          {activeTab === 'plans' && (
            <PlanComparison
              plans={plans}
              currentTier={userProfile?.subscription_tier}
              onSelectPlan={handlePlanChange}
              userProfile={userProfile}
            />
          )}

          {activeTab === 'payment' && (
            <PaymentMethods
              paymentMethods={paymentMethods}
              userId={user?.id}
              onUpdate={handlePaymentMethodUpdate}
            />
          )}

          {activeTab === 'invoices' && (
            <InvoiceHistory invoices={invoices} onViewAll={() => {}} />
          )}
        </div>
      </main>
    </div>
  );
};

export default BillingDashboard;